import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ReturnPaymentGl from "./ReturnPaymentGl";
import MoneyInput from "../MoneyInput";
import {
  MONTHS, manilaToday, manilaDateOf, bir_date, money, rateLabel, statusChips, Chip, displayValue, ItemNo, ItemRow, Part,
  inputClass, AmberNote, TaxpayerSizeBanner, FiledBadge, PaymentDetails, paymentsFromView, paymentPayload, mapFieldErrors,
  FileConfirmModal, useReturnActions,
} from "./returnUi";
import { pesosToCentavos } from "../../utils/whtMoney";

// BIR Form 1601-EQ (January 2019 ENCS) for one client and year —
// docs/WHT_IMPLEMENTATION.md §6. Item numbers and labels follow the form so
// staff can copy them into eBIRForms (no PDF, D8).

const HEADER = [
  ["1", "For the Year", "text"],
  ["2", "Quarter", "text"],
  ["3", "Amended Return?", "bool"],
  ["4", "Any Taxes Withheld?", "bool"],
  ["5", "No. of Sheet/s Attached", "text"],
];
const PART_I = [
  ["6", "TIN", "text"],
  ["7", "RDO Code", "text"],
  ["8", "Withholding Agent's Name", "text"],
  ["9", "Registered Address", "text"],
  ["9A", "ZIP Code", "text"],
  ["10", "Contact Number", "text"],
  ["11", "Category of Withholding Agent", "category"],
  ["12", "Email Address", "text"],
];
const ATC_ITEMS = ["13", "14", "15", "16", "17", "18"];
const PAYMENT_ROWS = [
  ["32", "cash", "Cash/Bank Debit Memo"],
  ["33", "check", "Check"],
  ["34", "tdm", "Tax Debit Memo"],
  ["35", "other", "Others (specify)"],
];
export const REFUND_OPTIONS = [
  ["refund", "To be refunded"],
  ["tcc", "To be issued Tax Credit Certificate"],
  ["carry_over", "To be carried over to the next quarter within the same calendar year"],
];

const quarterMonths = (q) => [q * 3 - 2, q * 3 - 1, q * 3].map((m) => MONTHS[m - 1]).join("–");

/** "from 0619-E Oct, filed 11/09/2026" (amended: ", amended") — or why there is none. */
export function sourceLabel(src) {
  if (!src) return "";
  const m = MONTHS[src.month - 1];
  if (!src.filedAt) return `no filed 0619-E for ${m}`;
  return `from 0619-E ${m}, filed ${bir_date(manilaDateOf(src.filedAt))}${src.isAmended ? " (amended)" : ""}`;
}

const AtcTable = ({ caption, lines, itemNos }) => (
  <table className="w-full text-sm" aria-label={caption}>
    <thead>
      <tr className="text-xs font-medium text-ink-body uppercase tracking-wider">
        <th className="text-left py-1 w-14"><span className="sr-only">Item</span></th>
        <th className="text-left py-1">ATC</th>
        <th className="text-right py-1">Tax Base (Consolidated for the Quarter)</th>
        <th className="text-right py-1">Tax Rate</th>
        <th className="text-right py-1">Tax Withheld (Consolidated for the Quarter)</th>
      </tr>
    </thead>
    <tbody>
      {lines.map((l, i) => (
        <tr key={itemNos ? itemNos[i] : `att-${i}`} className="border-t border-ink-line" data-atc-line={itemNos ? itemNos[i] : `attached-${i + 1}`}>
          <td className="py-2">{itemNos ? <ItemNo no={itemNos[i]} /> : <span className="text-xs text-ink-body">{i + 1}</span>}</td>
          <td className="py-2 text-ink">{l?.atcCode || "—"}</td>
          <td className="py-2 text-right tabular-nums text-ink">{l ? money(l.taxBaseCentavos) : "—"}</td>
          <td className="py-2 text-right tabular-nums text-ink">{l ? rateLabel(l.rateBasisPoints) : "—"}</td>
          <td className="py-2 text-right tabular-nums text-ink">{l ? money(l.taxWithheldCentavos) : "—"}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Form1601EQTab = ({ client, year, onGoToEwtRecords, onOpenPostingAccounts }) => {
  const clientId = client?._id;
  const today = manilaToday();
  const [summary, setSummary] = useState(null);
  const [quarter, setQuarter] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmFile, setConfirmFile] = useState(false);
  const [filedDate, setFiledDate] = useState(today);
  const [missingTin, setMissingTin] = useState(null); // rows from MISSING_PAYEE_TIN

  const loadSummary = useCallback(async () => {
    if (!clientId || !year) return;
    try {
      setSummary((await withholdingTaxAPI.get1601eqYear(clientId, year)).data);
    } catch (e) {
      setError(e.message);
    }
  }, [clientId, year]);

  useEffect(() => { setQuarter(null); setView(null); loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (quarter || !summary) return;
    const open = summary.quarters.find((q) => q.status !== "filed");
    setQuarter((open || summary.quarters[summary.quarters.length - 1]).quarter);
  }, [summary, quarter]);

  const loadView = useCallback(async () => {
    if (!clientId || !year || !quarter) return;
    setLoading(true);
    setError(null);
    try {
      const d = (await withholdingTaxAPI.get1601eq(clientId, year, quarter)).data;
      setView(d);
      const m = d.manualItems || {};
      setDraft({
        payments: paymentsFromView(PAYMENT_ROWS, d.payment),
        penaltyAuto: d.penaltyAuto !== false,
        surcharge: (d.manualSurchargeCentavos || 0) / 100,
        interest: (d.manualInterestCentavos || 0) / 100,
        compromise: (d.manualCompromiseCentavos || 0) / 100,
        override20: m["20"] !== undefined ? m["20"] / 100 : null,
        override21: m["21"] !== undefined ? m["21"] / 100 : null,
        other24: (m["24"] || 0) / 100,
        refundOption: d.refundOption ?? null,
      });
      setFieldErrors({});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, year, quarter]);

  useEffect(() => { setMissingTin(null); loadView(); }, [loadView]);

  const draftPayload = () => ({
    payment: paymentPayload(PAYMENT_ROWS, draft.payments),
    penaltyAuto: draft.penaltyAuto,
    manualSurchargeCentavos: pesosToCentavos(draft.surcharge) || 0,
    manualInterestCentavos: pesosToCentavos(draft.interest) || 0,
    manualCompromiseCentavos: pesosToCentavos(draft.compromise) || 0,
    manualItems: {
      20: draft.override20 === null ? null : pesosToCentavos(draft.override20) || 0,
      21: draft.override21 === null ? null : pesosToCentavos(draft.override21) || 0,
      24: pesosToCentavos(draft.other24) || 0,
    },
    refundOption: draft.refundOption,
  });

  const afterChange = async (data) => {
    setView(data);
    await Promise.all([loadSummary(), loadView()]);
  };

  const { runSave, saving, runFile, filing, runAmend, amending } = useReturnActions({
    label: `1601-EQ · Q${quarter} ${year}`,
    saveRequest: () => {
      const payload = draftPayload();
      return withholdingTaxAPI.save1601eqDraft(clientId, year, quarter, payload).catch((e) => {
        e.payload = payload;
        throw e;
      });
    },
    fileRequest: (filedAt) => withholdingTaxAPI.file1601eq(clientId, year, quarter, filedAt),
    amendRequest: () => withholdingTaxAPI.amend1601eq(clientId, year, quarter),
    onChanged: async (data) => { setMissingTin(null); await afterChange(data); },
    onSaveError: (e) => setFieldErrors(mapFieldErrors(e, e.payload)),
    onFileError: (e) => {
      if (e.code === "MISSING_PAYEE_TIN") {
        setMissingTin(e.response?.data?.rows || []);
        setConfirmFile(false);
        return true;
      }
      return false;
    },
    onFiled: () => setConfirmFile(false),
  });

  const byQuarter = useMemo(() => new Map((summary?.quarters || []).map((q) => [q.quarter, q])), [summary]);
  const readOnly = view?.readOnly;
  const items = view?.items || {};
  const overRemitted = Number(items["26"]) < 0;
  const needsOption = !readOnly && overRemitted && !draft?.refundOption;
  const warningsFor = (itemNo) => (view?.warnings || []).filter((w) => w.item === itemNo);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setPayment = (type, patch) => setDraft((d) => ({ ...d, payments: { ...d.payments, [type]: { ...d.payments[type], ...patch } } }));

  const remittanceRow = (no, label) => {
    const key = no === "20" ? "override20" : "override21";
    const overridden = draft[key] !== null;
    const src = view.itemSources?.[no];
    const computed = view.computedItems?.[no] ?? 0;
    return (
      <ItemRow
        key={no}
        no={no}
        label={(
          <span>
            {label}
            {src && (
              <span className="block text-xs text-ink-body" data-testid={`source-${no}`}>
                {overridden ? `Entered by hand (computed ${money(computed)}, ${sourceLabel(src)})` : sourceLabel(src)}
              </span>
            )}
          </span>
        )}
        note={warningsFor(no).map((w) => <AmberNote key={w.code}>{w.message}</AmberNote>)}
      >
        {readOnly ? money(items[no]) : (
          <span className="flex items-center gap-2 justify-end">
            {overridden ? (
              <>
                <MoneyInput id={`item-${no}-override`} value={draft[key]} onValueChange={(v) => set({ [key]: v })} className={`${inputClass} w-36 text-right`} />
                <Button variant="ghost" size="sm" onClick={() => set({ [key]: null })}>Use 0619-E value</Button>
              </>
            ) : (
              <>
                <span>{money(items[no])}</span>
                <Button variant="ghost" size="sm" onClick={() => set({ [key]: computed / 100 })} aria-label={`Override item ${no}`}>Override</Button>
              </>
            )}
          </span>
        )}
      </ItemRow>
    );
  };

  const moneyRow = (no, label, editableField) => (
    <ItemRow key={no} no={no} label={label}>
      {!readOnly && editableField ? (
        <MoneyInput id={`item-${no}-input`} value={draft[editableField]} onValueChange={(v) => set({ [editableField]: v })} className={`${inputClass} w-36 text-right`} />
      ) : money(items[no])}
    </ItemRow>
  );

  return (
    <div>
      <div role="group" aria-label="Quarter" className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {[1, 2, 3, 4].map((q) => {
          const s = byQuarter.get(q);
          return (
            <button
              key={q}
              type="button"
              aria-pressed={quarter === q}
              onClick={() => setQuarter(q)}
              className={`flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left text-sm ${
                quarter === q ? "border-primary-500 bg-primary-50" : "border-ink-line bg-white"
              }`}
            >
              <span className="font-medium text-ink">Q{q} <span className="font-normal text-ink-body">· {quarterMonths(q)}</span></span>
              {s && (
                <span className="flex flex-wrap gap-1" data-testid={`chips-q${q}`}>
                  {statusChips(s, today).map((c) => <Chip key={c} label={c} />)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && <p role="alert" className="text-sm text-danger-500 mb-3">{error}</p>}
      {loading && !view && <p className="text-sm text-ink-body">Loading…</p>}

      {view && draft && (
        <div>
          {view.flags?.includes("NEEDS_TAXPAYER_SIZE") && <TaxpayerSizeBanner clientId={clientId} />}

          {missingTin && (
            <div role="alert" aria-label="Payees without a TIN" className="mb-4 border border-danger-200 bg-danger-50 rounded-lg p-3">
              <p className="text-sm font-medium text-danger-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Can't file yet — {missingTin.length} EWT row{missingTin.length !== 1 ? "s have" : " has"} a payee with no TIN (the QAP lists every payee by TIN)
              </p>
              <table className="w-full text-sm mt-2">
                <thead>
                  <tr className="text-xs text-danger-700 uppercase tracking-wider">
                    <th className="text-left py-1">Payee</th>
                    <th className="text-left py-1">Month</th>
                    <th className="text-left py-1">ATC</th>
                    <th className="text-right py-1">Tax base</th>
                    <th className="text-right py-1">Tax withheld</th>
                  </tr>
                </thead>
                <tbody>
                  {missingTin.map((r) => (
                    <tr key={r._id} className="border-t border-danger-200" data-testid="missing-tin-row">
                      <td className="py-1 text-ink">{r.counterpartyName}</td>
                      <td className="py-1 text-ink">{MONTHS[r.month - 1]}</td>
                      <td className="py-1 text-ink">{r.atcCode}</td>
                      <td className="py-1 text-right tabular-nums text-ink">{money(r.baseCentavos)}</td>
                      <td className="py-1 text-right tabular-nums text-ink">{money(r.taxWithheldCentavos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-danger-700 mt-2">Add the TIN to each vendor, then open the row in EWT Records and save it again.</p>
              {onGoToEwtRecords && (
                <Button variant="outline" size="sm" className="mt-2" onClick={onGoToEwtRecords}>Go to EWT Records</Button>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-ink">BIR Form 1601-EQ · Q{quarter} {year}</h3>
              {view.isAmended && <Chip label={readOnly ? "Amended" : "Amendment draft"} />}
              {readOnly && <FiledBadge filedAt={view.filedAt} />}
            </div>
            <div className="flex items-center gap-2">
              {readOnly ? (
                <Button variant="outline" onClick={() => runAmend()} loading={amending}>Amend</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => runSave()} loading={saving}>Save draft</Button>
                  <Button onClick={() => setConfirmFile(true)} disabled={needsOption}>Mark as filed</Button>
                </>
              )}
            </div>
          </div>

          {needsOption && (
            <p className="text-xs text-warning-700 mb-3" data-testid="needs-option">
              This return shows an over-remittance — choose what to do with it (below item 31) before filing.
            </p>
          )}
          {!readOnly && view.penaltyAuto && (
            <p className="text-xs text-ink-body mb-3">
              Due {bir_date(view.adjustedDueDate)}. Penalties are shown as if filed today ({bir_date(view.penaltyAsOf)}) and are recomputed with the filing date.
            </p>
          )}

          {/* G2: payment journal entry of the filed version. */}
          <ReturnPaymentGl client={client} form="1601eq" view={view} onOpenPostingAccounts={onOpenPostingAccounts}
            onChanged={() => Promise.all([loadSummary(), loadView()])} />

          <Part title="Return period">
            {HEADER.map(([no, label, kind]) => (
              <ItemRow key={no} no={no} label={label}>{no === "2" ? `Q${items[no] ?? quarter}` : displayValue(kind, items[no])}</ItemRow>
            ))}
          </Part>

          <Part title="Part I – Background Information">
            {PART_I.map(([no, label, kind]) => <ItemRow key={no} no={no} label={label}>{displayValue(kind, items[no])}</ItemRow>)}
          </Part>

          <Part title="Part II – Computation of Tax">
            <AtcTable caption="Items 13 to 18" lines={ATC_ITEMS.map((no) => items[no] || null)} itemNos={ATC_ITEMS} />
            {view.attachedLines?.length > 0 && (
              <div className="mt-3 border-t border-ink-line pt-3">
                <h5 className="text-sm font-medium text-ink mb-1">
                  Attached sheet{items["5"] > 1 ? "s" : ""} ({items["5"]}) — {view.attachedLines.length} more ATC line{view.attachedLines.length !== 1 ? "s" : ""}
                </h5>
                <AtcTable caption="Attached sheet" lines={view.attachedLines} />
              </div>
            )}
            <div className="mt-3">
              <ItemRow no="19" label="Total Taxes Withheld for the Quarter (Sum of Items 13 to 18)">{money(items["19"])}</ItemRow>
              {remittanceRow("20", "Less: Remittances Made: 1st Month of the Quarter")}
              {remittanceRow("21", "2nd Month of the Quarter")}
              <ItemRow no="22" label="Tax Remitted in Return Previously Filed, if this is an amended return">{money(items["22"])}</ItemRow>
              <ItemRow
                no="23"
                label="Over-remittance from Previous Quarter of the same taxable year"
                note={Number(items["23"]) > 0 && <p className="mt-1 ml-[4.25rem] text-xs text-ink-body">Carried over from Q{quarter - 1}.</p>}
              >
                {money(items["23"])}
              </ItemRow>
              {moneyRow("24", "Other Payments Made (please attach proof of payments - BIR Form No. 0605)", "other24")}
              <ItemRow no="25" label="Total Remittances Made (Sum of Items 20 to 24)">{money(items["25"])}</ItemRow>
              <ItemRow no="26" label="Tax Still Due/(Over-remittance) (Item 19 Less Item 25)">{money(items["26"])}</ItemRow>
              {moneyRow("27", "Add: Penalties — Surcharge", draft.penaltyAuto ? null : "surcharge")}
              {moneyRow("28", "Interest", draft.penaltyAuto ? null : "interest")}
              {moneyRow("29", "Compromise", "compromise")}
              <ItemRow no="30" label="Total Penalties (Sum of Items 27 to 29)">{money(items["30"])}</ItemRow>
              <ItemRow no="31" label="TOTAL AMOUNT STILL DUE/(Over-remittance) (Sum of Items 26 and 30)">{money(items["31"])}</ItemRow>
            </div>

            {(overRemitted || view.refundOption) && (
              <fieldset className="mt-3 border border-ink-line rounded-md p-3" aria-label="Over-remittance option">
                <legend className="text-xs font-medium text-ink-body px-1">In case of over-remittance</legend>
                {REFUND_OPTIONS.map(([value, label]) => {
                  const q4Blocked = value === "carry_over" && quarter === 4;
                  return (
                    <label
                      key={value}
                      className={`flex items-center gap-2 text-sm py-1 ${q4Blocked ? "text-ink-body cursor-not-allowed" : "text-ink"}`}
                      title={q4Blocked ? "Not allowed in the 4th quarter — the over-remittance cannot be carried into the next year" : undefined}
                    >
                      <input
                        type="radio"
                        name="refundOption"
                        value={value}
                        checked={(readOnly ? view.refundOption : draft.refundOption) === value}
                        disabled={readOnly || q4Blocked}
                        onChange={() => set({ refundOption: value })}
                        className="h-4 w-4 accent-primary-500"
                      />
                      {label}
                    </label>
                  );
                })}
              </fieldset>
            )}

            {!readOnly && (
              <label className="flex items-center gap-2 text-sm text-ink mt-3">
                <input type="checkbox" checked={draft.penaltyAuto} onChange={(e) => set({ penaltyAuto: e.target.checked })} className="h-4 w-4 accent-primary-500" />
                Compute surcharge and interest automatically (the compromise is always entered by hand)
              </label>
            )}
            {!readOnly && <p className="text-xs text-ink-body mt-1">Save the draft to recompute items 25 to 31.</p>}
          </Part>

          <PaymentDetails
            title="Part III – Details of Payment"
            rows={PAYMENT_ROWS}
            payments={draft.payments}
            readOnly={readOnly}
            fieldErrors={fieldErrors}
            onChange={setPayment}
          />

          <FileConfirmModal
            isOpen={confirmFile}
            title={`Mark the Q${quarter} ${year} 1601-EQ as filed?`}
            filedDate={filedDate}
            onDateChange={setFiledDate}
            loading={filing}
            onConfirm={() => runFile(filedDate)}
            onCancel={() => setConfirmFile(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Form1601EQTab;
